/**
 * LAE Productivity (Schools mode): the period picker.
 *
 * Ported from the LAE app `src/features/productivity/PeriodControl.jsx`: the five preset segments, a
 * previous / day / next stepper for a single day, and From / To dates for a custom window. A pure
 * controlled component; every value lives in the URL through P4 (`lae-params.ts`), which owns
 * `setPeriod` (wired to `onChange`) and `stepDay` (wired to `onStep`).
 *
 * Deliberate differences from LAE:
 *   - Built from EMERGE primitives: `SegmentedControl` for the presets, icon-only `Button`s for the
 *     stepper, `Input type="date"` and `Field` for the dates.
 *   - LAE's day readout (long day text that turns into a native picker on click) is replaced by the
 *     native date input itself: one control fewer, and it works on touch (spec 2.2).
 *   - No tooltips on the presets or the date. Icon-only buttons show their name; Next day at today
 *     shows why it is disabled (spec 0.4).
 *   - A typed or picked date after today is clamped to today, on the custom bounds as well as the day.
 *   - A partial or invalid date from the native input is ignored, like LAE's empty-value guard.
 *   - The segmented control wraps below `sm`, so the five presets fit a 320px screen.
 */
import { Button, Field, Input, SegmentedControl, type SegmentedControlOption } from '@/components/ui';
import { ChevronIcon } from '@/components/catalog/icons';
import { cx } from '@/lib/cx';
import { isIsoDate, todayIst, type LaeDateRange } from './lae-dates';

/** The five period presets, in display order. */
export type PeriodPreset = 'day' | '7d' | '30d' | '12w' | 'custom';

/** What `onChange` receives: LAE `setPeriod({ preset, day?, from?, to? })`. */
export interface PeriodChange {
  preset: PeriodPreset;
  day?: string;
  from?: string;
  to?: string;
}

export interface PeriodControlProps {
  /** The current preset (P4 `preset`). An unknown value selects no segment. */
  preset: string;
  /** The current day for the `day` preset (P4 `day`); empty means today. */
  day: string | null | undefined;
  /** The window the current period selects (P4 `range`). */
  range: LaeDateRange | null | undefined;
  onChange: (next: PeriodChange) => void;
  /** Step the day by `n` (P4 `stepDay`); it refuses a day after today itself. */
  onStep: (n: number) => void;
  /** Smaller controls, for the School modal. */
  compact?: boolean;
}

const PRESET_OPTIONS: readonly SegmentedControlOption<PeriodPreset>[] = [
  { value: 'day', label: 'Day' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '12w', label: '12 weeks' },
  { value: 'custom', label: 'Custom' },
];

export function PeriodControl({ preset, day, range, onChange, onStep, compact = false }: PeriodControlProps) {
  const today = todayIst();
  const currentDay = day || today;
  const atToday = currentDay >= today;
  const size = compact ? 'sm' : 'md';
  const clamp = (v: string) => (v > today ? today : v);

  const choose = (p: PeriodPreset) => {
    if (p === preset) return;
    if (p === 'day') onChange({ preset: 'day', day: currentDay });
    else if (p === 'custom') {
      const next: PeriodChange = { preset: 'custom' };
      if (range?.from) next.from = range.from;
      if (range?.to) next.to = range.to;
      onChange(next);
    } else onChange({ preset: p });
  };

  const setCustom = (key: 'from' | 'to', value: string) => {
    if (!isIsoDate(value)) return;
    const next: PeriodChange = { preset: 'custom' };
    if (range?.from) next.from = range.from;
    if (range?.to) next.to = range.to;
    next[key] = clamp(value);
    onChange(next);
  };

  return (
    <div className={cx('flex min-w-0 max-w-full flex-wrap items-end', compact ? 'gap-1.5' : 'gap-2')}>
      <SegmentedControl
        label="Period"
        value={preset as PeriodPreset}
        options={PRESET_OPTIONS}
        onChange={choose}
        size="sm"
        className="max-w-full flex-wrap sm:w-auto"
      />

      {preset === 'day' && (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size={size}
            iconOnly
            aria-label="Previous day"
            leadingIcon={<ChevronIcon direction="left" />}
            onClick={() => onStep(-1)}
          />
          <Input
            type="date"
            aria-label="Day"
            value={currentDay}
            max={today}
            onChange={(e) => {
              const v = e.target.value;
              if (isIsoDate(v)) onChange({ preset: 'day', day: clamp(v) });
            }}
            className="w-[150px] tabular-nums"
          />
          <Button
            variant="ghost"
            size={size}
            iconOnly
            aria-label="Next day"
            leadingIcon={<ChevronIcon direction="right" />}
            disabled={atToday}
            {...(atToday ? { title: 'Today is the latest day' } : {})}
            onClick={() => onStep(1)}
          />
        </div>
      )}

      {preset === 'custom' && (
        <div className="flex min-w-0 max-w-full flex-wrap items-end gap-2">
          <Field label="From">
            {(field) => (
              <Input
                {...field}
                type="date"
                value={range?.from ?? ''}
                max={range?.to || today}
                onChange={(e) => setCustom('from', e.target.value)}
                className="w-[150px] tabular-nums"
              />
            )}
          </Field>
          <Field label="To">
            {(field) => (
              <Input
                {...field}
                type="date"
                value={range?.to ?? ''}
                {...(range?.from ? { min: range.from } : {})}
                max={today}
                onChange={(e) => setCustom('to', e.target.value)}
                className="w-[150px] tabular-nums"
              />
            )}
          </Field>
        </div>
      )}
    </div>
  );
}

export default PeriodControl;
